import { motion } from "framer-motion";
import { TrendingUp, FileText, CalendarCheck, FolderOpen, Shield, Wallet, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LucideIcon } from "lucide-react";

interface WorkerCard {
  /** anchor id, kept in sync with AGENTS in @/lib/agents */
  anchorId: string;
  nameKey: string;
  roleKey: string;
  abilityKey: string;
  roiKey: string;
  roiType: "up" | "down";
  icon: LucideIcon;
  textColor: string;
  subTextColor: string;
  iconColor: string;
  cardBg: string;
}

const workers: WorkerCard[] = [
  // NovaRate - 深蓝渐变
  { anchorId: "novarate", nameKey: "workerCard.rate.name", roleKey: "workerCard.rate.role", abilityKey: "workerCard.rate.ability", roiKey: "workerCard.rate.roi", roiType: "up", icon: TrendingUp, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-blue-600 via-blue-700 to-slate-900" },
  // NovaQuote - 紫色
  { anchorId: "novaquote", nameKey: "workerCard.quote.name", roleKey: "workerCard.quote.role", abilityKey: "workerCard.quote.ability", roiKey: "workerCard.quote.roi", roiType: "up", icon: FileText, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-violet-500 to-purple-600" },
  // NovaBook - 绿色
  { anchorId: "novabook", nameKey: "workerCard.book.name", roleKey: "workerCard.book.role", abilityKey: "workerCard.book.ability", roiKey: "workerCard.book.roi", roiType: "up", icon: CalendarCheck, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-emerald-500 to-teal-600" },
  // NovaDocs - 青绿色
  { anchorId: "novadocs", nameKey: "workerCard.docs.name", roleKey: "workerCard.docs.role", abilityKey: "workerCard.docs.ability", roiKey: "workerCard.docs.roi", roiType: "down", icon: FolderOpen, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-teal-500 to-green-600" },
  // NovaCustoms - 靛蓝色
  { anchorId: "novacustoms", nameKey: "workerCard.customs.name", roleKey: "workerCard.customs.role", abilityKey: "workerCard.customs.ability", roiKey: "workerCard.customs.roi", roiType: "down", icon: Shield, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-indigo-500 to-blue-600" },
  // NovaFinance - 玫瑰色
  { anchorId: "novafinance", nameKey: "workerCard.finance.name", roleKey: "workerCard.finance.role", abilityKey: "workerCard.finance.ability", roiKey: "workerCard.finance.roi", roiType: "down", icon: Wallet, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-rose-500 to-pink-600" },
  // NovaRisk - 橙色
  { anchorId: "novarisk", nameKey: "workerCard.risk.name", roleKey: "workerCard.risk.role", abilityKey: "workerCard.risk.ability", roiKey: "workerCard.risk.roi", roiType: "down", icon: AlertTriangle, textColor: "text-white", subTextColor: "text-white/70", iconColor: "text-white/30", cardBg: "bg-gradient-to-br from-orange-500 to-red-600" },
];

export function DigitalWorkerCards() {
  const { t } = useLanguage();

  const firstWorker = workers[0]!;
  const FirstIcon = firstWorker.icon;
  const otherWorkers = workers.slice(1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mt-10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 auto-rows-auto">
        {/* 左侧大卡片 - 跨2行 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className={`lg:row-span-2 ${firstWorker.cardBg} rounded-2xl p-6 flex flex-col justify-between min-h-[320px] lg:min-h-[400px] relative overflow-hidden`}
        >
          {/* 背景装饰 */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute top-1/4 right-0 w-32 h-32 bg-blue-400 rounded-full blur-3xl" />
          </div>
          
          <div className="relative z-10">
            <p className="text-white/70 text-sm mb-2">{t(firstWorker.roleKey)}</p>
            <h3 className="text-white text-3xl font-bold leading-tight">
              {t(firstWorker.nameKey)}
            </h3>
          </div>
          
          <div className="relative z-10 mt-auto">
            <p className="text-white/80 text-sm mb-3">{t(firstWorker.abilityKey)}</p>
            <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/20 text-red-300`}>
              <span>{t(firstWorker.roiKey)}</span>
              <span>↑</span>
            </div>
          </div>
        </motion.div>

        {/* NovaQuote - 占两列 */}
        {(() => {
          const quoteWorker = otherWorkers[0]!;
          const QuoteIcon = quoteWorker.icon;
          return (
            <motion.div
              key={quoteWorker.nameKey}
              id={quoteWorker.anchorId}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className={`lg:col-span-2 ${quoteWorker.cardBg} scroll-mt-24 rounded-2xl p-5 flex flex-col min-h-[160px] relative overflow-hidden group hover:shadow-lg transition-shadow duration-300`}
            >
              <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                <QuoteIcon className={`w-16 h-16 ${quoteWorker.iconColor}`} />
              </div>
              <div className="relative z-10">
                <p className={`text-xs mb-1 ${quoteWorker.subTextColor}`}>{t(quoteWorker.roleKey)}</p>
                <h4 className={`text-xl font-bold mb-2 ${quoteWorker.textColor}`}>{t(quoteWorker.nameKey)}</h4>
              </div>
              <div className="relative z-10 mt-auto">
                <p className={`text-xs mb-2 ${quoteWorker.subTextColor}`}>{t(quoteWorker.abilityKey)}</p>
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-white/20 text-white">
                  <span>{t(quoteWorker.roiKey)}</span>
                  <span>↑</span>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* NovaBook + NovaDocs - 合并占一个位置 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl overflow-hidden flex flex-col min-h-[160px] group hover:shadow-lg transition-shadow duration-300"
        >
          <div className="flex flex-col h-full">
            {[otherWorkers[1]!, otherWorkers[2]!].map((worker, idx) => {
              const Icon = worker.icon;
              return (
                <div key={worker.nameKey} className={`flex-1 flex flex-col justify-between p-3 ${worker.cardBg}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-[10px] ${worker.subTextColor}`}>{t(worker.roleKey)}</p>
                      <h4 className={`text-sm font-bold ${worker.textColor}`}>{t(worker.nameKey)}</h4>
                    </div>
                    <Icon className={`w-5 h-5 ${worker.iconColor}`} />
                  </div>
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium w-fit bg-white/20 text-white`}>
                    <span>{t(worker.roiKey)}</span>
                    <span>{worker.roiType === "up" ? "↑" : "↓"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* 剩余3个卡片 */}
        {otherWorkers.slice(3).map((worker, idx) => {
          const Icon = worker.icon;
          
          return (
            <motion.div
              key={worker.nameKey}
              {...(worker.anchorId === "novafinance" || worker.anchorId === "novarisk"
                ? { id: worker.anchorId }
                : {})}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (idx + 3) * 0.05 }}
              className={`${worker.cardBg} scroll-mt-24 rounded-2xl p-5 flex flex-col min-h-[160px] relative overflow-hidden group hover:shadow-lg transition-shadow duration-300`}
            >
              <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                <Icon className={`w-16 h-16 ${worker.iconColor}`} />
              </div>
              <div className="relative z-10">
                <p className={`text-xs mb-1 ${worker.subTextColor}`}>{t(worker.roleKey)}</p>
                <h4 className={`text-xl font-bold mb-2 ${worker.textColor}`}>{t(worker.nameKey)}</h4>
              </div>
              <div className="relative z-10 mt-auto">
                <p className={`text-xs mb-2 ${worker.subTextColor}`}>{t(worker.abilityKey)}</p>
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-white/20 text-white`}>
                  <span>{t(worker.roiKey)}</span>
                  <span>{worker.roiType === "up" ? "↑" : "↓"}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
